/**
 * Visor de Genio Documental en VENTANA adicional (no pestaña), para poder
 * ver los documentos mientras se trabaja en CIMA. La ventana es nombrada:
 * cada apertura reutiliza la misma ventana lateral en vez de acumular
 * pestañas. Todas las pantallas deben abrir el visor con este helper.
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-07-30
 */

import { shootToast } from "codekit";

const GENIO_WINDOW_NAME = "genioVisor";

/**
 * Abre el visor de Genio en la ventana lateral, reutilizándola si existe.
 * Con windowName distinto se abren ventanas independientes (ej. comparar
 * los dos documentos de una duda de match lado a lado).
 * @param {string} path       Ruta/filtro del visor (ej. "JSONMETX->DOCNROXX=2667830")
 * @param {string} windowName Nombre de la ventana (default: la lateral única)
 */
const abrirVentanaLateral = (url, windowName) => {
  const width = Math.min(1100, Math.floor(window.screen.availWidth * 0.55));
  const height = window.screen.availHeight - 80;
  const left = window.screen.availWidth - width - 8;
  const features = `popup=yes,width=${width},height=${height},left=${left},top=40,resizable=yes,scrollbars=yes`;
  const genioWindow = window.open(url, windowName, features);
  if (genioWindow) {
    genioWindow.focus();
  }
  return genioWindow;
};

export const openGenioVisor = (path, windowName = GENIO_WINDOW_NAME) => {
  const baseUrl = process.env.REACT_APP_URL_VISOR_GENIO;
  if (!baseUrl) {
    return;
  }
  abrirVentanaLateral(`${baseUrl}/${path}`, windowName);
};

/** Empresa dueña de los paquetes en Genio (SIACO) */
const EMPNITXX_SIACO = "800251957";

/**
 * Abre el visor filtrado por los documentos de un DO, SIEMPRE con el NIT del
 * cliente (2026-09-24): en Genio conviven varios ambientes de CIMA y un mismo
 * número de DO puede pertenecer a otro cliente; por número solo, el DO 2697793
 * de Sodimac mostró facturas de Gecolsa. Si el que llama no conoce el cliente,
 * se resuelve contra la operación; sin cliente el visor NO se abre.
 * La ventana se abre en el clic (sincrónica) y se apunta cuando se resuelve el
 * cliente: así el navegador no la bloquea como popup.
 * @param {string|number} docnroxx Número del DO
 * @param {string} cliidxxx NIT del cliente (opcional: se resuelve si falta)
 */
export const openGenioVisorByDo = async (docnroxx, cliidxxx = "") => {
  const baseUrl = process.env.REACT_APP_URL_VISOR_GENIO;
  if (!baseUrl || !docnroxx) {
    return;
  }
  let cliente = String(cliidxxx ?? "").trim();
  const ventana = abrirVentanaLateral("about:blank", GENIO_WINDOW_NAME);
  if (cliente === "") {
    try {
      const { store } = await import("../libs/redux/store/store");
      const { DoApiSlice } = await import("../libs/redux/slices/Modimpxx/Trackimp/TrackimpApiSlice");
      const respuesta = await store.dispatch(DoApiSlice.endpoints.getClienteDeDo.initiate(String(docnroxx), { forceRefetch: true })).unwrap();
      cliente = String(respuesta?.data?.CLIIDXXX ?? "").trim();
    } catch (e) {
      cliente = "";
    }
  }
  if (cliente === "") {
    if (ventana) {
      ventana.close();
    }
    shootToast({ variant: "error", text: "No se pudo determinar el cliente de la operación: por confidencialidad el visor no se abre sin cliente.", autoHide: true, duration: 7000 });
    return;
  }
  const url = `${baseUrl}/EMPNITXX=${EMPNITXX_SIACO}:JSONMETX->DOCNROXX=${docnroxx}:JSONMETX->CLIIDXXX=${cliente}`;
  if (ventana) {
    ventana.location.href = url;
    ventana.focus();
  } else {
    abrirVentanaLateral(url, GENIO_WINDOW_NAME);
  }
};

/**
 * Sanitiza un número de documento para el filtro del visor (los "/" no
 * viajan en la URL del visor: se reemplazan por "---")
 * @param {string} docNumber Número del documento
 * @returns {string} Número sanitizado y URL-encoded
 */
const sanitizeDocNumber = (docNumber) => encodeURIComponent(String(docNumber).replace(/\//g, "---"));

/**
 * Abre el visor con una factura comercial Y SUS DOCUMENTOS HIJOS (lista de empaque, certificaciones): al resolver
 * un resultado clave de la factura, los hijos traen datos que ayudan (los pesos de la lista de empaque) y antes no
 * se veían desde aquí (Diego 2026-10-02). CIMA resuelve los paquetes de la factura y de sus hijos y el visor abre
 * con esa lista. Si no se pueden resolver, abre solo la factura por su número, como antes.
 * La ventana se abre en el clic (sincrónica) y se apunta cuando llegan los paquetes: así el navegador no la bloquea.
 * @param {string} facidxxx Número de la factura
 * @param {string} cliidxxx NIT del cliente
 * @param {string} idproxxx Proveedor (opcional)
 */
export const openGenioVisorByFactura = async (facidxxx, cliidxxx = "", idproxxx = "") => {
  const baseUrl = process.env.REACT_APP_URL_VISOR_GENIO;
  if (!facidxxx) {
    shootToast({
      variant: "warning",
      text: "No se encontró el soporte en genio documental.",
      duration: 5000,
      autoHide: true,
    });
    return;
  }
  if (!baseUrl) {
    return;
  }
  const cliente = String(cliidxxx ?? "").trim();
  //Con el NIT del cliente cuando la pantalla lo tiene (2026-09-24): un mismo numero de factura puede
  //existir para otro cliente en Genio.
  const filtroCliente = cliente !== "" ? `:JSONMETX->CLIIDXXX=${cliente}` : "";
  const soloLaFactura = `EMPNITXX=${EMPNITXX_SIACO}:AREAIDXX=100:SERIEIDX=102:TIPOIDXX=143:REGESTXX=ACTIVO:JSONMETX->FACOMERX=${sanitizeDocNumber(facidxxx)}${filtroCliente}`;
  const ventana = abrirVentanaLateral("about:blank", GENIO_WINDOW_NAME);
  let paquetes = [];
  let operaciones = [];
  if (cliente !== "") {
    try {
      const { store } = await import("../libs/redux/store/store");
      const { salfactxApiSlice } = await import("../libs/redux/slices/Faccomxx/Salfactx/salfactxApiSlice");
      const respuesta = await store
        .dispatch(salfactxApiSlice.endpoints.getPaquetesGenioFactura.initiate({ CLIIDXXX: cliente, IDPROXXX: String(idproxxx ?? ""), FACIDXXX: String(facidxxx) }, { forceRefetch: true }))
        .unwrap();
      paquetes = respuesta?.data?.IDARCHXX ?? [];
      operaciones = respuesta?.data?.DOCNROXX ?? [];
    } catch (e) {
      paquetes = [];
    }
  }
  //Con hijos, el visor recibe la lista de paquetes (padre e hijos). El visor solo dibuja la relación padre-hijo
  //cuando el filtro lleva la operación: se agrega cuando la factura es de una sola (los paquetes ya la acotan).
  const conRelacion = operaciones.length === 1 ? `:JSONMETX->DOCNROXX=${operaciones[0]}${filtroCliente}` : "";
  const url = `${baseUrl}/${paquetes.length > 1 ? `IDARCHXX[IN]${paquetes.join(",")}${conRelacion}` : soloLaFactura}`;
  if (ventana) {
    ventana.location.href = url;
    ventana.focus();
  } else {
    abrirVentanaLateral(url, GENIO_WINDOW_NAME);
  }
};

/**
 * Abre el visor apuntando a un paquete documental puntual
 * @param {string|number} idarchxx   IDARCHXX del paquete en Genio
 * @param {string}        windowName Opcional: nombre de ventana propio
 */
export const openGenioVisorByIdArchivo = (idarchxx, windowName) =>
  openGenioVisor(`IDARCHXX=${idarchxx}`, windowName);

/**
 * Abre el visor con varios paquetes documentales a la vez (PDF, imagen,
 * Excel...). Con uno solo abre ese paquete, igual que openGenioVisorByIdArchivo.
 * @param {Array<string|number>} idsArchivo IDARCHXX de los paquetes en Genio
 * @param {string}               windowName Opcional: nombre de ventana propio
 */
export const openGenioVisorByIdArchivos = (idsArchivo, windowName) => {
  const ids = [...new Set((idsArchivo ?? []).map(Number).filter((id) => id > 0))];
  if (ids.length > 1) {
    openGenioVisor(`IDARCHXX[IN]${ids.join(",")}`, windowName);
    return;
  }
  if (ids.length === 1) {
    openGenioVisorByIdArchivo(ids[0], windowName);
  }
};

//Lado en el que se abre la próxima ventana del modo comparar (alterna)
let comparadorLado = 0;

/**
 * Abre el visor en modo COMPARAR (gestión documental): cada documento
 * abre SU PROPIA ventana (nombrada por IDARCHXX, así reabrir el mismo
 * documento reutiliza su ventana) y las ventanas se acomodan alternando
 * mitad derecha / mitad izquierda de la pantalla, para poder ver dos
 * documentos lado a lado al decidir un match o una vinculación.
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-08-06
 * @param {string|number} idarchxx IDARCHXX del paquete en Genio
 */
export const openGenioVisorComparar = (idarchxx) => {
  const baseUrl = process.env.REACT_APP_URL_VISOR_GENIO;
  if (!baseUrl) {
    return;
  }
  const width = Math.floor(window.screen.availWidth * 0.48);
  const height = window.screen.availHeight - 80;
  const left = comparadorLado === 0 ? window.screen.availWidth - width - 8 : 8;
  comparadorLado = comparadorLado === 0 ? 1 : 0;
  const features = `popup=yes,width=${width},height=${height},left=${left},top=40,resizable=yes,scrollbars=yes`;
  const genioWindow = window.open(`${baseUrl}/IDARCHXX=${idarchxx}`, `genioVisor-${idarchxx}`, features);
  if (genioWindow) {
    genioWindow.focus();
  }
};

/**
 * Abre el visor filtrado por un documento de transporte. El tipo
 * documental y el campo de metadata dependen del medio de transporte
 * (1 marítimo BL, 3 terrestre CPIC, 4 aéreo AWB).
 * @param {object} dataEdit Fila del DT (DTRCDOTR, DTRCMETR)
 */
export const openGenioVisorByDoctra = (dataEdit) => {
  const docNumber = dataEdit?.DTRCDOTR || "";
  if (docNumber === "") {
    shootToast({
      variant: "warning",
      text: "No se encontró el soporte en genio documental.",
      duration: 5000,
      autoHide: true,
    });
    return;
  }
  const genioByMedio = {
    1: { TIPOIDXX: 562, field: "NUMBLXXX" },
    3: { TIPOIDXX: 101, field: "DOCTRANS" },
    4: { TIPOIDXX: 147, field: "DOCTRANS" },
  };
  const config = genioByMedio[String(parseInt(dataEdit?.DTRCMETR, 10))] || genioByMedio[4];
  //Con el NIT del cliente del DT (2026-09-24): un mismo numero de guia puede existir para otro cliente
  const cliente = String(dataEdit?.DTRCNICO ?? "").trim();
  openGenioVisor(
    `EMPNITXX=800251957:AREAIDXX=100:SERIEIDX=100:TIPOIDXX=${config.TIPOIDXX}` +
      `:REGESTXX=ACTIVO:JSONMETX->${config.field}=${sanitizeDocNumber(docNumber)}` +
      (cliente !== "" ? `:JSONMETX->CLIIDXXX=${cliente}` : "")
  );
};
